import React, { createContext, useContext, useEffect, useState } from 'react'
import { supabase } from './supabaseClient'

const AuthContext = createContext({})

const ENCOMENDAR_COMO_KEY = 'encomendar_como_email'

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null)
  const [perfil, setPerfil] = useState(null)
  const [viewAsEmail, setViewAsEmail] = useState(null)
  const [encomendarComoEmail, setEncomendarComoEmailState] = useState(() => {
    try { return localStorage.getItem(ENCOMENDAR_COMO_KEY) || null } catch { return null }
  })
  const [loading, setLoading] = useState(true)
  const [dadosIncompletos, setDadosIncompletos] = useState(false)
  const [inPasswordRecovery, setInPasswordRecovery] = useState(false)

  // Setter público que persiste em localStorage
  const setEncomendarComoEmail = (email) => {
    const v = email || null
    setEncomendarComoEmailState(v)
    try {
      if (v) localStorage.setItem(ENCOMENDAR_COMO_KEY, v)
      else localStorage.removeItem(ENCOMENDAR_COMO_KEY)
    } catch { /* ignore */ }
  }

  // view_as fica em pendingViewAs até confirmar que o perfil é admin — evita
  // que se guarde valor em memória para não-admin. O gate final continua em L147.
  const [pendingViewAs, setPendingViewAs] = useState(null)
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search)
    const viewAs = urlParams.get('view_as')
    if (viewAs) setPendingViewAs(viewAs)

    // Deteção precoce de fluxo de recuperação: link do email vem com #type=recovery
    if (typeof window !== 'undefined' && window.location.hash?.includes('type=recovery')) {
      setInPasswordRecovery(true)
    }

    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null)
      if (session?.user) loadPerfil(session.user.id)
      else setLoading(false)
    })

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'PASSWORD_RECOVERY') {
        setInPasswordRecovery(true)
        setUser(session?.user ?? null)
        setLoading(false)
        return
      }
      setUser(session?.user ?? null)
      if (session?.user) loadPerfil(session.user.id)
      else { setPerfil(null); setLoading(false) }
    })

    return () => subscription.unsubscribe()
  }, [])

  const clearPasswordRecovery = () => setInPasswordRecovery(false)

  const loadPerfil = async (uid) => {
    try {
      const { data, error } = await supabase.from('perfis').select('*').eq('id', uid).single()
      if (error) {
        console.warn('Erro ao carregar perfil:', error.message)
        // Fallback: usar user_metadata do JWT como backup
        const { data: { user } } = await supabase.auth.getUser()
        const metaRole = user?.user_metadata?.role || 'cliente'
        const metaNome = user?.user_metadata?.nome || user?.email
        setPerfil({ id: uid, email: user?.email, role: metaRole, nome: metaNome, ativo: true })
      } else {
        // GATE: bloquear acesso a contas pendentes/recusadas (admin ainda nao aprovou)
        // Aplica-se a contas que se registaram e ficaram com sessao activa pos-signUp
        // (caso "Confirm Email" off no Supabase ou signUp + session imediato).
        if (data.role !== 'admin') {
          const estadoOk = data.estado_conta === 'ativo' && data.ativo === true
          if (!estadoOk) {
            console.warn('[gate] conta nao activa — forcando logout', data.estado_conta, data.ativo)
            await supabase.auth.signOut()
            setUser(null)
            setPerfil(null)
            setLoading(false)
            // Mensagem no localStorage para Login.jsx mostrar
            try {
              const msg = data.estado_conta === 'recusado'
                ? 'O seu registo foi recusado. Contacte o administrador para mais informacoes.'
                : data.estado_conta === 'pendente_email'
                ? 'Por favor valide o seu email clicando no link que lhe foi enviado.'
                : 'A sua conta esta pendente de aprovacao pelo administrador. Sera notificado por email quando for activada.'
              window.localStorage.setItem('login_gate_msg', msg)
            } catch {}
            return
          }
        }
        setPerfil(data)
        // Verificar se dados obrigatórios estão preenchidos (apenas para clientes)
        if (data.role === 'cliente') {
          const dadosObrigatorios = data.nome && data.email && data.telefone && data.morada && data.cod_postal
          setDadosIncompletos(!dadosObrigatorios)
        }
      }
    } catch (err) {
      console.error('Erro crítico ao carregar perfil:', err)
      setPerfil(null)
    } finally {
      setLoading(false)
    }
  }

  const signIn = async (email, password) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password })
    if (error) return { data, error }
    // Verificar estado da conta
    const uid = data?.user?.id
    if (uid) {
      const { data: p } = await supabase.from('perfis').select('estado_conta, ativo').eq('id', uid).single()
      if (p?.estado_conta === 'pendente_email') {
        await supabase.auth.signOut()
        return { data: null, error: { message: 'Por favor valide o seu email clicando no link que lhe foi enviado. Após validação, o administrador será notificado para aprovar o seu registo.' } }
      }
      if (p?.estado_conta === 'pendente' || p?.ativo === false && p?.estado_conta !== 'ativo') {
        await supabase.auth.signOut()
        return { data: null, error: { message: 'A sua conta está pendente de aprovação pelo administrador. Será notificado por email quando for ativada.' } }
      }
      if (p?.estado_conta === 'recusado') {
        await supabase.auth.signOut()
        return { data: null, error: { message: 'O seu registo foi recusado. Contacte o administrador para mais informações.' } }
      }
    }
    return { data, error }
  }

  const signOut = async () => {
    await supabase.auth.signOut()
    setUser(null)
    setPerfil(null)
  }

  const isAdmin = perfil?.role === 'admin'
  const isCliente = perfil?.role === 'cliente'

  // Ao confirmar admin, promover pendingViewAs → viewAsEmail. Caso contrário limpar.
  useEffect(() => {
    if (isAdmin && pendingViewAs) setViewAsEmail(pendingViewAs)
    else if (!isAdmin) setViewAsEmail(null)
  }, [isAdmin, pendingViewAs])

  return (
    <AuthContext.Provider value={{ user, perfil, loading, isAdmin, isCliente, signIn, signOut, loadPerfil, viewAsEmail: isAdmin ? viewAsEmail : null, encomendarComoEmail: isAdmin ? encomendarComoEmail : null, setEncomendarComoEmail, dadosIncompletos, inPasswordRecovery, clearPasswordRecovery }}>
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = () => useContext(AuthContext)
